import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { playPracticeSound } from '../services/soundEffects';
import { t } from '../i18n';
import './CelebrationPopup.css';

function CelebrationPopup({
  isVisible,
  onClose,
  title,
  message,
  iconSrc,
  iconAlt,
  iconLayout = 'default',
  presentation = 'dialog',
  colorTheme = 'default',
  animationSpriteSrc,
  animationVideoWebmSrc,
  animationVideoMp4Src,
  animationVideoPosterSrc,
  animationFrameCount = 0,
  animationFrameSize = 128,
  animationDurationMs = 720,
  managedFocus = false,
  returnFocusRef = null,
}) {
  const [isClosing, setIsClosing] = useState(false);
  const [isAnimationComplete, setIsAnimationComplete] = useState(false);
  const [reducedMotion, setReducedMotion] = useState(() => typeof window !== 'undefined'
    && Boolean(window.matchMedia?.('(prefers-reduced-motion: reduce)').matches));
  const [videoFailed, setVideoFailed] = useState(false);
  const [visualViewportStyle, setVisualViewportStyle] = useState(null);
  const closeTimerRef = useRef(null);
  const animationTimerRef = useRef(null);
  const closingRef = useRef(false);
  const dialogRef = useRef(null);
  const closeButtonRef = useRef(null);
  const closeHandlerRef = useRef(null);
  const hasAnimation = Boolean(animationSpriteSrc || animationVideoWebmSrc || animationVideoMp4Src);

  useEffect(() => {
    const media = window.matchMedia?.('(prefers-reduced-motion: reduce)');
    if (!media) return undefined;
    const update = () => setReducedMotion(media.matches);
    update();
    media.addEventListener?.('change', update);
    return () => media.removeEventListener?.('change', update);
  }, []);

  const handleClose = useCallback((options = {}) => {
    if (closingRef.current) return;
    closingRef.current = true;

    const immediate = options?.immediate === true;
    if (immediate) {
      if (closeTimerRef.current) {
        clearTimeout(closeTimerRef.current);
        closeTimerRef.current = null;
      }
      onClose?.();
      return;
    }

    setIsClosing(true);
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
    }

    closeTimerRef.current = setTimeout(() => {
      closeTimerRef.current = null;
      setIsClosing(false);
      onClose?.();
    }, 260);
  }, [onClose]);
  closeHandlerRef.current = handleClose;

  useEffect(() => {
    if (isVisible) {
      playPracticeSound('celebration');
      closingRef.current = false;
      setVideoFailed(false);
      setIsClosing(false);
      setIsAnimationComplete(!hasAnimation || reducedMotion);

      if (animationTimerRef.current) {
        clearTimeout(animationTimerRef.current);
      }

      if (hasAnimation && !reducedMotion) {
        animationTimerRef.current = setTimeout(() => {
          animationTimerRef.current = null;
          setIsAnimationComplete(true);
        }, animationDurationMs);
      }
    }
  }, [animationDurationMs, hasAnimation, isVisible, reducedMotion]);

  useEffect(() => {
    return () => {
      if (closeTimerRef.current) {
        clearTimeout(closeTimerRef.current);
      }
      if (animationTimerRef.current) {
        clearTimeout(animationTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!isVisible || typeof window === 'undefined' || !window.visualViewport) {
      setVisualViewportStyle(null);
      return undefined;
    }

    const viewport = window.visualViewport;
    const updateViewport = () => {
      setVisualViewportStyle({
        inset: 'auto',
        left: `${viewport.offsetLeft}px`,
        top: `${viewport.offsetTop}px`,
        width: `${viewport.width}px`,
        height: `${viewport.height}px`,
      });
    };

    updateViewport();
    viewport.addEventListener('resize', updateViewport);
    viewport.addEventListener('scroll', updateViewport);
    return () => {
      viewport.removeEventListener('resize', updateViewport);
      viewport.removeEventListener('scroll', updateViewport);
    };
  }, [isVisible]);

  useEffect(() => {
    if (!isVisible || managedFocus) return undefined;

    const handleKeyDown = (event) => {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation?.();
      if (event.repeat) return;
      handleClose({ immediate: true });
    };

    window.addEventListener('keydown', handleKeyDown, true);
    return () => window.removeEventListener('keydown', handleKeyDown, true);
  }, [handleClose, isVisible, managedFocus]);

  useEffect(() => {
    if (!isVisible || !managedFocus) return undefined;
    const previousFocus = document.activeElement;
    const fallbackFocus = returnFocusRef?.current;
    (closeButtonRef.current || dialogRef.current)?.focus({ preventScroll: true });
    const handleKeyDown = event => {
      // Keep card shortcuts outside this modal from reacting to dialog keys.
      event.stopPropagation();
      event.stopImmediatePropagation?.();
      if (event.key === 'Tab') {
        event.preventDefault();
        const controls = Array.from(dialogRef.current?.querySelectorAll('button:not(:disabled), a[href], input:not(:disabled), select:not(:disabled), textarea:not(:disabled), [tabindex]:not([tabindex="-1"])') || []);
        if (!controls.length) { dialogRef.current?.focus({ preventScroll: true }); return; }
        const index = controls.indexOf(document.activeElement);
        const nextIndex = event.shiftKey
          ? (index <= 0 ? controls.length - 1 : index - 1)
          : (index + 1) % controls.length;
        controls[nextIndex].focus({ preventScroll: true });
        return;
      }
      const closeActivation = event.target === closeButtonRef.current && ['Enter', ' '].includes(event.key);
      if (event.key === 'Escape' || closeActivation) {
        event.preventDefault();
        if (!event.repeat) closeHandlerRef.current?.({ immediate: true });
      }
    };
    window.addEventListener('keydown', handleKeyDown, true);
    return () => {
      window.removeEventListener('keydown', handleKeyDown, true);
      const canRestore = node => node?.isConnected && node !== document.body && !node.disabled && !node.closest?.('[inert]');
      const target = canRestore(previousFocus) ? previousFocus : fallbackFocus;
      if (canRestore(target)) target.focus?.({ preventScroll: true });
    };
  }, [isVisible, managedFocus, returnFocusRef]);

  if (!isVisible) {
    return null;
  }

  const usesWideIcon = iconLayout === 'wide' && iconSrc && !hasAnimation;
  const isFloating = presentation === 'floating';
  const isTransparentDialog = presentation === 'transparent-dialog';

  const popup = (
    <div
      className={`celebration-overlay ${isFloating ? 'celebration-overlay-floating' : ''} ${isTransparentDialog ? 'celebration-overlay-transparent-dialog' : ''} ${isClosing ? 'closing' : ''}`.trim()}
      style={visualViewportStyle || undefined}
      onClick={() => handleClose()}
      role="presentation"
    >
      <div
        ref={dialogRef}
        tabIndex={managedFocus ? -1 : undefined}
        className={`celebration-popup ${iconLayout === 'speech' ? 'celebration-popup-speech' : ''} ${colorTheme === 'level-up' ? 'celebration-popup-level-up' : ''} ${isFloating ? 'celebration-popup-floating' : ''} ${isTransparentDialog ? 'celebration-popup-transparent-dialog' : ''} ${isClosing ? 'closing' : ''}`.trim()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="celebration-title"
        aria-describedby="celebration-message"
        onClick={managedFocus ? event => event.stopPropagation() : undefined}
      >
        <button
          ref={closeButtonRef}
          className="celebration-close-btn"
          onClick={() => handleClose()}
          aria-label={t('common.close')}
          type="button"
        >
          x
        </button>

        <div className="celebration-content">
          {(hasAnimation || iconSrc) && (
            <div
              className={`celebration-visual-stage ${usesWideIcon ? 'celebration-visual-stage-wide' : ''}`.trim()}
              style={usesWideIcon
                ? undefined
                : { width: `${animationFrameSize}px`, height: `${animationFrameSize}px` }}
            >
              {animationSpriteSrc && (
                <div
                  className={`celebration-sprite ${isAnimationComplete || reducedMotion ? 'complete' : 'playing'}`.trim()}
                  aria-hidden="true"
                  style={{
                    width: `${animationFrameSize}px`,
                    height: `${animationFrameSize}px`,
                    backgroundImage: `url(${animationSpriteSrc})`,
                    backgroundSize: `${animationFrameSize * Math.max(animationFrameCount, 1)}px ${animationFrameSize}px`,
                    animationDuration: `${animationDurationMs}ms`,
                    animationTimingFunction: `steps(${Math.max(animationFrameCount, 1)})`,
                    '--celebration-sprite-play-end-x': `-${animationFrameSize * Math.max(animationFrameCount, 1)}px`,
                    '--celebration-sprite-final-x': `-${animationFrameSize * Math.max(animationFrameCount - 1, 0)}px`,
                  }}
                />
              )}

              {(animationVideoWebmSrc || animationVideoMp4Src) && (animationVideoPosterSrc && (reducedMotion || videoFailed) ? (
                <img className="celebration-video" src={animationVideoPosterSrc} alt="" aria-hidden="true" />
              ) : (
                <video
                  className="celebration-video"
                  aria-hidden="true"
                  autoPlay={!reducedMotion}
                  muted
                  playsInline
                  preload="auto"
                  poster={animationVideoPosterSrc || undefined}
                  onError={() => setVideoFailed(true)}
                >
                  {animationVideoWebmSrc && <source src={animationVideoWebmSrc} type="video/webm" />}
                  {animationVideoMp4Src && <source src={animationVideoMp4Src} type="video/mp4" />}
                </video>
              ))}

              {iconSrc && isAnimationComplete && (
                <img
                  className={`celebration-icon revealed ${usesWideIcon ? 'celebration-icon-wide' : ''}`.trim()}
                  src={iconSrc}
                  alt={iconAlt || ''}
                />
              )}
            </div>
          )}

          <div className="celebration-message">
            <h2 id="celebration-title">{title}</h2>
            <p id="celebration-message" className="celebration-subtitle">
              {message}
            </p>
          </div>
        </div>
      </div>
    </div>
  );

  if (typeof document === 'undefined' || !document.body) {
    return popup;
  }

  return createPortal(popup, document.body);
}

export default CelebrationPopup;
